// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetProjectHistorySegmentSchema } from '@/asset/api/get-asset-project-history';
import { DateTime } from 'luxon';
import { z } from 'zod';

const responseSchema = z
  .object({
    data: assetProjectHistorySegmentSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, segment: data }));

interface UpdateAssetProjectAssignmentParameters {
  start: DateTime;
  end: DateTime | null;
}

/**
 * Updates an existing asset project assignment.
 *
 * @param projectKey - The project key of the assignment.
 * @param segmentKey - The segment key of the assignment.
 * @param parameters.start - The start date of the assignment.
 * @param parameters.end - The end date of the assignment. This can be null if
 * the assignment is the most recent assignment for the asset.
 */
export async function updateAssetProjectAssignment(
  projectKey: string,
  segmentKey: string,
  parameters: UpdateAssetProjectAssignmentParameters
) {
  const path = `/v1/project/${projectKey}/segment/${segmentKey}`;
  const response = await api.patch(path, {
    effectiveDate: parameters.start.toISO(),
    endDate: parameters.end === null ? null : parameters.end.toISO(),
  });

  return parseResponse(responseSchema, response.data, {
    schemaName: 'UpdateAssetProjectAssignment',
    endpoint: path,
  });
}
